/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import Toast from 'components/atoms/Toast'
import { useWriteOffPatientBalanceMutation } from 'store/services/billingPayments'
import { PATIENT_WRITE_OFF_REASONS, isPatientWriteOffReason } from 'ts-common/utils/writeOffReasons.utils'

interface Props {
  patientId: number
  chargeId: string
  cptCode: string
  startDos?: string | null
  /** What the patient still owes on this line — the default and the cap. */
  patientBalance: number
  onClose: () => void
  onDone: () => void
}

/**
 * Forgives some or all of the patient's remaining responsibility on one
 * service line — no money moves. The row it creates reduces the patient
 * balance only; the payer-side Write-off column is a different animal (it
 * settles the plan balance) and is untouched by this.
 *
 * The server enforces every rule again: the amount caps (line and charge), the
 * reason, and the unapplied-credit block — a patient with credit on file must
 * have it applied or refunded first, or the forgiveness would strand their
 * money.
 */
export const PatientWriteOffModal: React.FC<Props> = ({
  patientId,
  chargeId,
  cptCode,
  startDos,
  patientBalance,
  onClose,
  onDone,
}) => {
  const [amount, setAmount] = useState(patientBalance.toFixed(2))
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [writeOff, { isLoading }] = useWriteOffPatientBalanceMutation()

  const parsedAmount = Number(amount)
  const amountInvalid = !Number.isFinite(parsedAmount)
    || parsedAmount <= 0
    || Number(parsedAmount.toFixed(2)) > patientBalance
  const reasonInvalid = !isPatientWriteOffReason(reason)

  const submit = async () => {
    setSubmitted(true)

    if (amountInvalid || reasonInvalid) {
      return
    }

    try {
      await writeOff({
        patientId,
        chargeId,
        cptCode,
        startDos,
        amount: Number(parsedAmount.toFixed(2)),
        reason,
        ...(note.trim() ? { note: note.trim() } : {}),
      }).unwrap()

      Toast({ type: 'success', label: `Wrote off $${parsedAmount.toFixed(2)} of patient balance on ${cptCode}` })
      onDone()
      onClose()
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message
        || 'Failed to write off the patient balance'
      Toast({ type: 'error', label: message })
    }
  }

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header>
        <Modal.Title css={{ fontSize: '1.1rem' }}>
          Write off patient balance — {cptCode}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p css={{ fontSize: '0.875rem', color: '#6c757d' }}>
          Reduces what the patient owes on this line without recording a payment.
          The write-off appears as its own row under the procedure and can be
          reversed like any other transaction.
        </p>
        <div className="form-group">
          <label css={{ fontSize: '0.813rem', fontWeight: 600 }}>
            Amount (up to ${patientBalance.toFixed(2)})
          </label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            max={patientBalance}
            className="form-control"
            value={amount}
            disabled={isLoading}
            onChange={(event) => setAmount(event.target.value)}
            css={submitted && amountInvalid ? { borderColor: '#dc3545' } : undefined}
          />
          {submitted && amountInvalid && (
            <div css={{ color: '#dc3545', fontSize: '0.75rem', marginTop: '0.2rem' }}>
              Enter an amount between $0.01 and ${patientBalance.toFixed(2)}
            </div>
          )}
        </div>
        <div className="form-group">
          <label css={{ fontSize: '0.813rem', fontWeight: 600 }}>Reason</label>
          <select
            className="form-control"
            value={reason}
            disabled={isLoading}
            onChange={(event) => setReason(event.target.value)}
            css={submitted && reasonInvalid ? { borderColor: '#dc3545' } : undefined}>
            <option value="">Reason…</option>
            {PATIENT_WRITE_OFF_REASONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {submitted && reasonInvalid && (
            <div css={{ color: '#dc3545', fontSize: '0.75rem', marginTop: '0.2rem' }}>
              A reason is required
            </div>
          )}
        </div>
        <div className="form-group">
          <label css={{ fontSize: '0.813rem', fontWeight: 600 }}>
            Note (optional)
          </label>
          <textarea
            className="form-control"
            rows={2}
            value={note}
            disabled={isLoading}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" disabled={isLoading} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" disabled={isLoading} onClick={submit}>
          {isLoading ? 'Writing off…' : 'Write Off'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
